import React, { useRef, useState } from 'react'
import styled from 'styled-components'
import CameraIcon from '../../icons/IconLib/CameraIcon'
import FolderIcon from '../../icons/IconLib/FolderIcon'
import { AccentButton } from '../Button'
import { TextAreaField } from '../Input'
import { FormFooter } from '../FormLayout'
import PhotoCaptureTile from './PhotoCaptureTile'
import PhotoSlotTile from './PhotoSlotTile'
import UploadStatusCard from './UploadStatusCard'
import useCameraPreview from './useCameraPreview'
import useFileUpload from './useFileUpload'
import useFilePreviewUrl from './useFilePreviewUrl'
import { formatFileSize } from './fileUpload.utils'

export interface PhotoUploadPanelProps {
  accept: string[]
  maxSizeMb?: number
  commentLabel?: string
  commentPlaceholder?: string
  commentMaxLength?: number
  /** Always receives `files` — may be empty when only a comment is sent. */
  onUpload: (payload: { files: File[]; comment: string }) => Promise<unknown>
  onUploaded?: () => void
  closeFunc?: () => void
}

const DEFAULT_MAX_SIZE_MB = 10
const DEFAULT_COMMENT_MAX_LENGTH = 500
/** Two square slots plus the wide camera cell, which holds the third photo. */
const SIDE_SLOT_COUNT = 2
const MAX_PHOTOS = SIDE_SLOT_COUNT + 1
/** Index of the photo shown inside the camera cell. */
const CAMERA_SLOT_INDEX = SIDE_SLOT_COUNT
// The whole set re-stages when a photo is added, so a long animation would
// blink the tiles that are already attached.
const PHOTO_STAGING_MS = 300

const TERMINAL_STATUSES = ['uploading', 'success', 'error']

/**
 * Photo-only upload modal content: a wide camera cell spanning two rows and two
 * square slots beside it, comment section underneath. The camera cell doubles as
 * the third slot — once it holds a photo, removing it brings the viewfinder back.
 * Formats and the size limit are validated but deliberately never shown.
 */
const PhotoUploadPanel = ({
  accept,
  maxSizeMb = DEFAULT_MAX_SIZE_MB,
  commentLabel,
  commentPlaceholder,
  commentMaxLength = DEFAULT_COMMENT_MAX_LENGTH,
  onUpload,
  onUploaded,
  closeFunc,
}: PhotoUploadPanelProps) => {
  const [text, setText] = useState<string>('')
  const trimmedText = text.trim()
  const cameraInputRef = useRef<HTMLInputElement>(null)
  const galleryInputRef = useRef<HTMLInputElement>(null)

  const {
    files,
    status,
    errorMessage,
    canUpload,
    selectFiles,
    removeFile,
    startUpload,
    reset,
  } = useFileUpload({
    accept,
    maxSizeMb,
    maxFiles: MAX_PHOTOS,
    countRows: false,
    stagingMs: PHOTO_STAGING_MS,
    onSuccess: onUploaded,
    allowFilelessUpload: trimmedText.length > 0,
    onUpload: (_file, picked) => onUpload({ files: picked, comment: trimmedText }),
  })

  // Slot count is a constant, so the hook order stays static.
  const firstPreview = useFilePreviewUrl(files[0] ?? null, true)
  const secondPreview = useFilePreviewUrl(files[1] ?? null, true)
  const cameraPreview = useFilePreviewUrl(files[CAMERA_SLOT_INDEX] ?? null, true)
  const sidePreviews = [firstPreview, secondPreview]

  const isTerminal = TERMINAL_STATUSES.includes(status)
  // The stream is only worth holding while the viewfinder is actually on screen:
  // otherwise the camera indicator would stay lit behind the result screens.
  const { videoRef, status: cameraStatus } = useCameraPreview(!cameraPreview && !isTerminal)

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    selectFiles(Array.from(e.target.files ?? []))
    // The same photo can be re-picked after an error.
    e.target.value = ''
  }

  // Removing photos keeps the typed text; starting over clears both.
  const handleResetAll = () => {
    setText('')
    reset()
  }

  const openCamera = () => cameraInputRef.current?.click()
  const openGallery = () => galleryInputRef.current?.click()

  const previewOf = (index: number) =>
    index === CAMERA_SLOT_INDEX ? cameraPreview : sidePreviews[index]

  if (status === 'uploading' || status === 'success') {
    const isDone = status === 'success'
    return (
      <Panel>
        <CardList>
          {files.length === 0 ? (
            <UploadStatusCard variant={isDone ? 'success' : 'uploading'} fileName={'הערה'} />
          ) : (
            files.map((file, index) => (
              <UploadStatusCard
                key={`${file.name}-${index}`}
                variant={isDone ? 'success' : 'uploading'}
                fileName={file.name}
                fileSizeText={formatFileSize(file.size)}
                previewUrl={previewOf(index)}
              />
            ))
          )}
        </CardList>
        {isDone && (
          <Footer>
            <AccentButton variant='ghost' fullWidth={false} onClick={handleResetAll}>
              העלאה נוספת
            </AccentButton>
            <AccentButton variant='accent' fullWidth={false} onClick={closeFunc}>
              סיום
            </AccentButton>
          </Footer>
        )}
      </Panel>
    )
  }

  if (status === 'error') {
    return (
      <Panel>
        <UploadStatusCard
          variant='error'
          fileName={files[0]?.name ?? ''}
          errorMessage={errorMessage}
        />
        <Footer>
          <AccentButton variant='ghost' fullWidth={false} onClick={closeFunc}>
            ביטול
          </AccentButton>
          <AccentButton variant='accent' fullWidth={false} onClick={startUpload}>
            ניסיון חוזר
          </AccentButton>
        </Footer>
      </Panel>
    )
  }

  return (
    <Panel>
      <Grid>
        <CameraCell>
          {cameraPreview ? (
            <PhotoSlotTile
              fill
              previewUrl={cameraPreview}
              label={`תמונה ${MAX_PHOTOS}`}
              onPick={openCamera}
              onRemove={() => removeFile(CAMERA_SLOT_INDEX)}
            />
          ) : (
            <PhotoCaptureTile
              videoRef={videoRef}
              status={cameraStatus}
              disabled={false}
              onClick={openCamera}
            />
          )}
        </CameraCell>
        {sidePreviews.map((previewUrl, index) => (
          <PhotoSlotTile
            key={index}
            previewUrl={previewUrl}
            label={`תמונה ${index + 1}`}
            onPick={openGallery}
            onRemove={() => removeFile(index)}
          />
        ))}
      </Grid>

      {errorMessage && <ErrorLine role='alert'>{errorMessage}</ErrorLine>}

      <PickRow>
        <PickButton type='button' onClick={openGallery}>
          <FolderIcon size={15} />
          בחירת קבצים
        </PickButton>
        <PickButton type='button' onClick={openCamera} aria-label='צילום' title='צילום'>
          <CameraIcon size={16} />
        </PickButton>
      </PickRow>

      {/* Capture opens the native camera, so the photo keeps its full quality. */}
      <HiddenInput
        ref={cameraInputRef}
        type='file'
        accept='image/*'
        capture='environment'
        onChange={handleInputChange}
      />
      <HiddenInput
        ref={galleryInputRef}
        type='file'
        accept={accept.join(',')}
        multiple
        onChange={handleInputChange}
      />

      <TextAreaField
        name='photo-upload-comment'
        label={commentLabel ?? 'הערה'}
        placeholder={commentPlaceholder}
        maxLength={commentMaxLength}
        showCounter
        value={text}
        onChange={(e) => setText(e.target.value)}
      />

      <Footer>
        <AccentButton variant='ghost' fullWidth={false} onClick={closeFunc}>
          ביטול
        </AccentButton>
        <AccentButton
          variant={canUpload ? 'accent' : 'ghost'}
          fullWidth={false}
          disabled={!canUpload}
          onClick={startUpload}
        >
          שליחה
        </AccentButton>
      </Footer>
    </Panel>
  )
}

export default PhotoUploadPanel

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
  direction: rtl;
`

/**
 * RTL: the first column sits at the inline-start, i.e. on the RIGHT — the camera
 * takes the wide column and spans both rows, the photo slots stack to its left.
 */
const Grid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  grid-template-rows: repeat(2, auto);
  gap: 0.625rem;
`

const CameraCell = styled.div`
  grid-row: 1 / span 2;
  min-height: 0;
`

const CardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const ErrorLine = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: ${({ theme }) => theme.text.sm};
  color: ${({ theme }) => theme.colors.danger};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.textMobile.sm};
  }
`

const PickRow = styled.div`
  display: flex;
  gap: 0.5rem;
`

/** Same shape as FileDropzone.PickButton — the fallback pickers read as one family. */
const PickButton = styled.button`
  height: 2.375rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0 1rem;
  border-radius: ${({ theme }) => theme.radii.md};
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: ${({ theme }) => theme.text.md};
  font-weight: 500;
  cursor: pointer;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.inputBorder};
  color: ${({ theme }) => theme.colors.ink2};
  transition:
    background ${({ theme }) => theme.motion.fast} ease,
    border-color ${({ theme }) => theme.motion.fast} ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.inputBorderHover};
    background: ${({ theme }) => theme.colors.bgLayout};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadows.focus};
  }
`

const HiddenInput = styled.input`
  display: none;
`

/** Sticky like the form modals' footer — stays visible while the panel scrolls. */
const Footer = styled(FormFooter)`
  justify-content: center;
  gap: 0.625rem;
`
